import {
  Button,
  Distribute,
  Spacer,
  SvgTrash,
  Switch,
  Text,
  TextButton,
} from '@loomhq/lens';
import Mousetrap from 'mousetrap';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import { showConfirmationModal } from 'actions/confirmationModal';
import {
  endThumbnailFlow,
  initiateThumbnailSave,
  setLocalThumbnail,
} from 'actions/editor';
import { showErrorBar } from 'actions/errorBar';
import { ERROR } from 'constants/error-severities';
import { GIF_SETTINGS_UPDATE } from 'constants/events';
import { ESCAPE_LITERAL } from 'constants/keyCodes';
import { KEY_UP } from 'constants/keyEvents';
import { removeVideoThumbnail, updateGifSettings } from 'creators/shareVideo';
import * as analytics from 'utilities/analytics';
import { getCloudfrontURI } from 'utilities/avatar';

import SharePageDropzone from '../share-page-dropzone';
import VideoSectionCard from '../video-section-card';
import {
  OG_THUMB_FULL,
  THUMB_FULL,
} from '../../../../../shared/constants/video';

import Checkbox from 'components/ui-components/checkbox';
import UploadIcon from 'components/ui-components/upload-icon';

import './styles.less';

class ThumbnailWidget extends React.Component {
  state = {
    useGifDefault: null,
    useGif: null,
    interactedWith: false,
  };

  render() {
    const {
      localThumbnail,
      uploading,
      thumbnails,
      preferredDims,
      supportGifs,
    } = this.props;
    const { interactedWith, useGifDefault, useGif } = this.state;

    const hasThumbnail = (thumbnails || {})[THUMB_FULL];
    const isGifSettings = !localThumbnail && interactedWith;

    return (
      <VideoSectionCard className="thumbnail-form">
        {/* make sure this is rendered first so the rest of the widget lies above it */}
        <SharePageDropzone multiple={false} onDrop={this.handleDropzoneDrop} />
        <div className="edit-thumbnail-header">
          <Distribute gap="auto">
            <Text variant="title">
              {hasThumbnail ? 'Edit thumbnail' : 'Add thumbnail'}
            </Text>
            <span className="navigation-close" onClick={this.handleExitFlow} />
          </Distribute>
        </div>
        <div className="thumbnail-drop-area">
          <div className="mb:small">
            <UploadIcon color="var(--lns-color-orangeDark)" size={6} />
          </div>

          <Text>Drag and drop to upload</Text>
          <Text size="small" color="bodyDimmed">
            {preferredDims.width} x {preferredDims.height}px .pngs, and .jpgs
            work best!
          </Text>
        </div>
        {
          // spacer
          localThumbnail && <div>&nbsp;</div>
        }
        {hasThumbnail && (
          <Fragment>
            <div className="thumb-instructions">
              Adjust positioning and zoom
            </div>
            <Text alignment="center" size="small">
              delete to use a different thumbnail
            </Text>
            <Spacer top="medium" bottom="medium">
              <TextButton
                className="relative"
                icon={<SvgTrash />}
                onClick={this.handleDeleteThumbnail}
              >
                Delete Thumbnail
              </TextButton>
            </Spacer>
          </Fragment>
        )}
        {supportGifs && !hasThumbnail && !localThumbnail && (
          <div className="animated-preview">
            <div className="flex mb:small">
              <span className="grow:1" onClick={this.toggleProp('useGif')}>
                Animated Thumbnail (gif)
              </span>
              <Switch
                index="use-gif"
                isActive={useGif || false}
                onClick={this.toggleProp('useGif')}
              />
            </div>
            <div className="animated-preview-default">
              <Checkbox
                isChecked={useGifDefault || false}
                onEnable={this.toggleProp('useGifDefault')}
                onDisable={this.toggleProp('useGifDefault')}
              />
              <span onClick={this.toggleProp('useGifDefault')}>
                Use this setting for all videos
              </span>
            </div>
          </div>
        )}
        <div className="flex width:full mt:medium relative">
          {!isGifSettings && (
            <Button
              variant="primary"
              isDisabled={uploading}
              onClick={this.handleAddCoverClick}
              hasFullWidth
            >
              {localThumbnail ? 'Save' : 'Browse'}
            </Button>
          )}
          <input
            type="file"
            className="upload-thumbnail-input"
            id="upload-thumbnail-file"
            onClick={this.handleUploadCoverClick}
            onChange={this.handleBrowseInput}
            accept="image/jpeg,image/pjpeg,image/png"
          />
          {isGifSettings && (
            <Button
              variant="primary"
              isDisabled={uploading}
              onClick={this.handleSaveGif}
              hasFullWidth
            >
              Save
            </Button>
          )}
        </div>
      </VideoSectionCard>
    );
  }

  toggleProp = prop => () => {
    this.setState({ [prop]: !this.state[prop], interactedWith: true });
  };

  handleDeleteThumbnail = () => {
    const { videoId } = this.props;

    this.props.removeVideoThumbnail(videoId);
    this.props.setLocalThumbnail(null);
  };

  handleExitFlow = () => {
    this.props.setLocalThumbnail(null);
    this.props.endThumbnailFlow();
  };

  handleDropzoneDrop = files => {
    const file = files[0];

    if (!file) {
      return;
    }

    this.handleFile(file);
  };

  handleBrowseInput = event => {
    const {
      target: { files },
    } = event;
    const file = files[0];

    this.handleFile(file);

    event.target.value = null;
  };

  readFile = blob => {
    const reader = new FileReader();

    // Closure to capture the file information.
    reader.onload = e => {
      const imgSrc = e.target.result;
      const img = new Image();

      img.onload = () => {
        this.props.setLocalThumbnail(imgSrc, {
          width: img.width,
          height: img.height,
        });
      };
      img.src = imgSrc;
    };

    // Read in the image file as a data URL.
    reader.readAsDataURL(blob);
  };

  handleFile = file => {
    if (!file) {
      return;
    }

    this.readFile(file);
  };

  handleUploadCoverClick = event => {
    event.stopPropagation();
  };

  handleAddCoverClick = () => {
    if (this.props.localThumbnail) {
      this.props.initiateThumbnailSave();

      return;
    }

    document.querySelector('#upload-thumbnail-file').click();
  };

  handleSaveGif = () => {
    const { useGif, useGifDefault } = this.state;
    const { videoId } = this.props;

    analytics.track(GIF_SETTINGS_UPDATE, {
      useGif,
      useGifDefault,
      videoId,
    });

    this.props.updateGifSettings(videoId, useGif, useGifDefault && useGif);
  };

  componentDidMount() {
    const { thumbnails } = this.props;
    const ogThumbnail = (thumbnails || {})[OG_THUMB_FULL];
    const { useGif = false } = this.props;

    this.setState({ useGif });

    Mousetrap.bind(
      ESCAPE_LITERAL,
      () => {
        this.props.setLocalThumbnail(null);
        this.props.endThumbnailFlow();
      },
      KEY_UP
    );

    if (!ogThumbnail) {
      return;
    }

    fetch(getCloudfrontURI(ogThumbnail))
      .then(resp => {
        if (!resp.ok) {
          throw new Error('Failed to fetch current thumbnail');
        }

        return resp.blob();
      })
      .then(thumbBlob => {
        this.readFile(thumbBlob);
      })
      .catch(() => {
        this.props.showErrorBar(
          ERROR,
          'Oops! failed to fetch current thumbnail, please delete current thumbnail to upload new image'
        );
      });
  }

  componentWillUnmount() {
    Mousetrap.unbind(ESCAPE_LITERAL, KEY_UP);
  }
}

const mapStateToProps = state => {
  const {
    currentUserIsOwner,
    video,
    download_url: downloadUrl,
  } = state.share_video;
  const { width, height } = state.video_player;
  const { thumbnail_data } = state.editor;

  return {
    currentUserIsOwner,
    videoId: video.id,
    thumbnails: video.thumbnails,
    localThumbnail: thumbnail_data.thumbnail_local,
    uploading: thumbnail_data.uploading,
    preferredDims: { width, height },
    useGif: video.use_gif,
    supportGifs: downloadUrl,
  };
};

const mapDispatchToProps = dispatch => ({
  removeVideoThumbnail: videoId => dispatch(removeVideoThumbnail(videoId)),
  endThumbnailFlow: () => dispatch(endThumbnailFlow()),
  setLocalThumbnail: (url, dims) => dispatch(setLocalThumbnail(url, dims)),
  initiateThumbnailSave: () => dispatch(initiateThumbnailSave()),
  showErrorBar: (severity, message) =>
    dispatch(showErrorBar(severity, message)),
  showMessage: msg => dispatch(showConfirmationModal(msg)),
  updateGifSettings: (videoId, useGif, useGifDefault) =>
    dispatch(updateGifSettings(videoId, useGif, useGifDefault)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ThumbnailWidget);
